import { useState, useEffect } from "react"
import { getJobCategories, getGigTitlesByCategory } from "../../services/gig-service"
import { useCitiesByCounty } from "../../hooks/location-hooks"

interface County {
  code: string
  name: string
}

interface Category {
  id: string
  name: string
}

export type SortOption = "none" | "rating_desc" | "rating_asc"

export interface CandidateFilters {
  sort: SortOption
  county: string
  cityId: string
  availableOn: string
  availableFrom: string
  availableTo: string
  // "HH:mm", optionale. Se aplica peste oricare dintre cele doua moduri de mai
  // sus si se compara cu programul zilnic al candidatului.
  availableTimeFrom: string
  availableTimeTo: string
  gigTitle: string
  categoryId: string
  category: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  counties: County[]
  gigTitles: string[]
  filters: CandidateFilters
  onApply: (filters: CandidateFilters) => void
}

const sortOptions: { value: SortOption; label: string; icon: string }[] = [
  { value: "none", label: "Fără sortare", icon: "○" },
  { value: "rating_desc", label: "Rating descrescător", icon: "★" },
  { value: "rating_asc", label: "Rating crescător", icon: "☆" },
]

const selectClass =
  "w-full border border-[#E5E7EB] rounded-xl px-3 py-3 text-sm bg-white text-[#180E73] focus:outline-none focus:ring-2 focus:ring-figmaPrimary appearance-none cursor-pointer"

export const CandidatesFilterModal = ({
  isOpen,
  onClose,
  counties,
  gigTitles,
  filters,
  onApply,
}: Props) => {
  const [localSort, setLocalSort] = useState<SortOption>(filters.sort)
  const [localCounty, setLocalCounty] = useState(filters.county)
  const [localCityId, setLocalCityId] = useState(filters.cityId)
  const [localAvailableOn, setLocalAvailableOn] = useState(filters.availableOn)
  const [localAvailableFrom, setLocalAvailableFrom] = useState(filters.availableFrom)
  const [localAvailableTo, setLocalAvailableTo] = useState(filters.availableTo)
  const [localAvailableTimeFrom, setLocalAvailableTimeFrom] = useState(filters.availableTimeFrom)
  const [localAvailableTimeTo, setLocalAvailableTimeTo] = useState(filters.availableTimeTo)
  const [localGigTitle, setLocalGigTitle] = useState(filters.gigTitle)
  const [localCategoryId, setLocalCategoryId] = useState(filters.categoryId)
  const [localCategory, setLocalCategory] = useState(filters.category)

  const [categories, setCategories] = useState<Category[]>([])
  const [titlesForCategory, setTitlesForCategory] = useState<string[]>([])

  useEffect(() => {
    getJobCategories().then((data: Category[]) => setCategories(data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (isOpen) {
      setLocalSort(filters.sort)
      setLocalCounty(filters.county)
      setLocalCityId(filters.cityId)
      setLocalAvailableOn(filters.availableOn)
      setLocalAvailableFrom(filters.availableFrom)
      setLocalAvailableTo(filters.availableTo)
      setLocalAvailableTimeFrom(filters.availableTimeFrom)
      setLocalAvailableTimeTo(filters.availableTimeTo)
      setLocalGigTitle(filters.gigTitle)
      setLocalCategoryId(filters.categoryId)
      setLocalCategory(filters.category)
    }
  }, [isOpen, filters])

  useEffect(() => {
    if (localCategoryId) {
      getGigTitlesByCategory(localCategoryId)
        .then((data: string[]) => setTitlesForCategory(data))
        .catch(() => setTitlesForCategory([]))
    } else {
      setTitlesForCategory([])
    }
  }, [localCategoryId])

  const availableTitles = localCategoryId ? titlesForCategory : gigTitles

  // Orele se compara cu programul candidatului dintr-o zi anume, deci au sens
  // doar impreuna cu una dintre cele doua selectii de date.
  const hasDateFilter =
    !!localAvailableOn || !!(localAvailableFrom && localAvailableTo)
  const hasHourRange =
    !!localAvailableTimeFrom &&
    !!localAvailableTimeTo &&
    localAvailableTimeFrom !== localAvailableTimeTo
  const hourRangeIsIncomplete =
    hasDateFilter &&
    !!(localAvailableTimeFrom || localAvailableTimeTo) &&
    !hasHourRange

  // Se cer doar localitatile judetului selectat; lista completa are peste 3000 de randuri.
  const { cities: citiesForCounty, isLoading: isLoadingCities } = useCitiesByCounty(localCounty)

  const handleCountyChange = (county: string) => {
    setLocalCounty(county)
    setLocalCityId("")
  }

  // Orele raman fara sens de indata ce nu mai exista o zi selectata, iar
  // input-urile doar dezactivate ar continua sa arate valori care nu se aplica.
  const clearHoursWithoutDate = (on: string, from: string, to: string) => {
    if (on || (from && to)) return
    setLocalAvailableTimeFrom("")
    setLocalAvailableTimeTo("")
  }

  const handleCategoryChange = (categoryId: string, categoryName: string) => {
    setLocalCategoryId(categoryId)
    setLocalCategory(categoryName)
    setLocalGigTitle("")
  }

  const handleApply = () => {
    onApply({
      sort: localSort,
      county: localCounty,
      cityId: localCityId,
      availableOn: localAvailableOn,
      availableFrom: localAvailableFrom,
      availableTo: localAvailableTo,
      // O singura ora completata nu descrie un interval, iar fara o zi selectata
      // n-avem cu ce compara, deci in ambele cazuri ignoram orele.
      availableTimeFrom: hasDateFilter && hasHourRange ? localAvailableTimeFrom : "",
      availableTimeTo: hasDateFilter && hasHourRange ? localAvailableTimeTo : "",
      gigTitle: localGigTitle,
      categoryId: localCategoryId,
      category: localCategory,
    })
    onClose()
  }

  const handleReset = () => {
    onApply({ sort: "none", county: "", cityId: "", availableOn: "", availableFrom: "", availableTo: "", availableTimeFrom: "", availableTimeTo: "", gigTitle: "", categoryId: "", category: "" })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl flex flex-col max-h-[88vh] shadow-2xl">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-[#E5E7EB]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
          <span className="text-[17px] font-bold text-[#180E73]">
            Filtrează candidații
          </span>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-[#F2F4F6] flex items-center justify-center"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="#9F9F9F"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {/* SORTEAZA DUPA */}
          <SectionLabel text="SORTEAZĂ DUPĂ" />
          <div className="mt-3 mb-2 space-y-[10px]">
            {sortOptions.map((opt) => {
              const sel = localSort === opt.value
              return (
                <button
                  key={opt.value}
                  onClick={() => setLocalSort(opt.value)}
                  className={`w-full flex items-center gap-3 px-4 py-[14px] rounded-xl border text-left transition-colors ${
                    sel
                      ? "border-figmaPrimary border-[1.5px] bg-[#486af6]/[0.08]"
                      : "border-[#E5E7EB] bg-white"
                  }`}
                >
                  <span className={`text-lg leading-none ${sel ? "text-figmaPrimary" : "text-[#9F9F9F]"}`}>
                    {opt.icon}
                  </span>
                  <span className={`text-sm flex-1 ${sel ? "font-semibold text-figmaPrimary" : "font-normal text-[#180E73]"}`}>
                    {opt.label}
                  </span>
                  {sel && (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9s3.36 7.5 7.5 7.5 7.5-3.36 7.5-7.5S13.14 1.5 9 1.5zm-1.5 10.94L4.06 9l1.06-1.06L7.5 10.38l5.38-5.38 1.06 1.06-6.44 6.44z"
                        fill="#486af6"
                      />
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          <Divider />

          {/* JUDET */}
          <SectionLabel text="JUDEȚ" />
          <div className="mt-3 mb-2 relative">
            <select
              className={selectClass}
              value={localCounty}
              onChange={(e) => handleCountyChange(e.target.value)}
            >
              <option value="">Toate județele</option>
              {counties.map((co) => (
                <option key={co.code} value={co.code}>
                  {co.name}
                </option>
              ))}
            </select>
            <ChevronIcon />
          </div>

          <Divider />

          {/* LOCALITATE */}
          <SectionLabel text="LOCALITATE" />
          <div className="mt-3 mb-2 relative">
            <select
              className={selectClass}
              value={localCityId}
              onChange={(e) => setLocalCityId(e.target.value)}
              disabled={!localCounty || isLoadingCities}
            >
              <option value="">
                {!localCounty
                  ? "Selectează mai întâi județul"
                  : isLoadingCities
                    ? "Se încarcă..."
                    : "Toate localitățile"}
              </option>
              {citiesForCounty.map((city) => (
                <option key={city.id} value={String(city.id)}>
                  {city.name}
                </option>
              ))}
            </select>
            <ChevronIcon muted={!localCounty || isLoadingCities} />
          </div>

          <Divider />

          {/* DISPONIBILITATE */}
          <SectionLabel text="DISPONIBILITATE" />
          <div className="mt-3 mb-1">
            <label className="text-[11px] text-[#9F9F9F] uppercase tracking-wide mb-1 block">
              Disponibil în ziua
            </label>
            <input
              type="date"
              className={selectClass}
              value={localAvailableOn}
              onChange={(e) => {
                setLocalAvailableOn(e.target.value)
                if (e.target.value) {
                  setLocalAvailableFrom("")
                  setLocalAvailableTo("")
                }
                clearHoursWithoutDate(e.target.value, "", "")
              }}
            />
          </div>
          <div className="mt-3 mb-1">
            <label className="text-[11px] text-[#9F9F9F] uppercase tracking-wide mb-1 block">
              Sau disponibil în intervalul
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                className={`${selectClass} flex-1`}
                value={localAvailableFrom}
                onChange={(e) => {
                  setLocalAvailableFrom(e.target.value)
                  if (e.target.value) setLocalAvailableOn("")
                  clearHoursWithoutDate(
                    e.target.value ? "" : localAvailableOn,
                    e.target.value,
                    localAvailableTo
                  )
                }}
              />
              <span className="text-[#9F9F9F] text-sm">—</span>
              <input
                type="date"
                className={`${selectClass} flex-1`}
                value={localAvailableTo}
                onChange={(e) => {
                  setLocalAvailableTo(e.target.value)
                  if (e.target.value) setLocalAvailableOn("")
                  clearHoursWithoutDate(
                    e.target.value ? "" : localAvailableOn,
                    localAvailableFrom,
                    e.target.value
                  )
                }}
              />
            </div>
          </div>
          <div className="mt-3 mb-1">
            <label className="text-[11px] text-[#9F9F9F] uppercase tracking-wide mb-1 block">
              Interval orar
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="time"
                className={`${selectClass} flex-1 ${!hasDateFilter ? "opacity-50 cursor-not-allowed" : ""}`}
                value={localAvailableTimeFrom}
                disabled={!hasDateFilter}
                onChange={(e) => setLocalAvailableTimeFrom(e.target.value)}
              />
              <span className="text-[#9F9F9F] text-sm">—</span>
              <input
                type="time"
                className={`${selectClass} flex-1 ${!hasDateFilter ? "opacity-50 cursor-not-allowed" : ""}`}
                value={localAvailableTimeTo}
                disabled={!hasDateFilter}
                onChange={(e) => setLocalAvailableTimeTo(e.target.value)}
              />
            </div>
            <p className="mt-1 text-[11px] text-[#9F9F9F]">
              {!hasDateFilter
                ? "Alege întâi o zi sau o perioadă."
                : hourRangeIsIncomplete
                  ? "Completează ambele ore, cu valori diferite."
                  : "Se compară cu programul zilnic al candidatului. Lasă gol pentru orice oră."}
            </p>
          </div>

          <Divider />

          {/* CATEGORIE JOB */}
          <SectionLabel text="CATEGORIE JOB" />
          <div className="mt-3 mb-2 relative">
            <select
              className={selectClass}
              value={localCategoryId}
              onChange={(e) => {
                const cat = categories.find((c) => c.id === e.target.value)
                handleCategoryChange(e.target.value, cat?.name ?? "")
              }}
            >
              <option value="">Toate categoriile</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <ChevronIcon />
          </div>

          <Divider />

          {/* POZITIE */}
          <SectionLabel text="POZIȚIE" />
          <div className="mt-3 mb-2 relative">
            <select
              className={selectClass}
              value={localGigTitle}
              onChange={(e) => setLocalGigTitle(e.target.value)}
              disabled={localCategoryId !== "" && titlesForCategory.length === 0}
            >
              <option value="">
                {localCategoryId && titlesForCategory.length === 0
                  ? "Se încarcă..."
                  : "Toate pozițiile"}
              </option>
              {availableTitles.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </select>
            <ChevronIcon muted={localCategoryId !== "" && titlesForCategory.length === 0} />
          </div>

          <div className="h-6" />
        </div>

        {/* Bottom buttons */}
        <div className="flex-shrink-0 flex gap-3 px-5 pt-3 pb-7 border-t border-[#F3F4F6] bg-white">
          <button
            onClick={handleReset}
            className="flex-1 py-[14px] rounded-xl border border-[#E5E7EB] text-sm font-semibold text-[#9F9F9F] bg-white"
          >
            Resetează
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-[14px] rounded-xl bg-figmaPrimary text-sm font-semibold text-white"
          >
            Aplică
          </button>
        </div>
      </div>
    </div>
  )
}

const SectionLabel = ({ text }: { text: string }) => (
  <span className="text-[11px] font-semibold text-[#180E73] tracking-[0.8px] uppercase">
    {text}
  </span>
)

const Divider = () => <div className="my-4 h-px bg-[#F3F4F6]" />

const ChevronIcon = ({ muted = false }: { muted?: boolean }) => (
  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 6l4 4 4-4"
        stroke={muted ? "#D1D5DB" : "#9F9F9F"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
)
